import { createFileRoute, Link } from '@tanstack/react-router';
import { SHIRES } from '../components/shared/electorate-shires';

export const Route = createFileRoute('/new-here')({
  component: NewHerePage,
  head: () => ({
    meta: [
      { title: 'New here? A welcome checklist — Warren-Blackwood' },
      {
        name: 'description',
        content:
          'Just moved to Warren-Blackwood? A one-page checklist for new residents: update the electoral roll, find your shire and bin day, find a GP, enrol kids in school, get connected, and find the local library.',
      },
      { property: 'og:title', content: 'New here? A welcome checklist — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'A practical checklist for people who have just moved to Warren-Blackwood — councils, schools, GPs, NBN, libraries.',
      },
    ],
  }),
});

const card: React.CSSProperties = {
  background: 'var(--cream)',
  border: '1px solid var(--rule)',
  borderRadius: 8,
  padding: '1.1rem 1.2rem',
  marginBottom: 'var(--sp-md)',
};
const link: React.CSSProperties = {
  color: 'var(--rivergum)',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};
const h2: React.CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontSize: '1.2rem',
  color: 'var(--green)',
  margin: '0 0 0.2rem',
};
const stepNum: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: 'var(--green)',
  color: 'var(--cream)',
  fontFamily: 'var(--font-heading)',
  fontSize: '0.95rem',
  marginRight: '0.65rem',
  flexShrink: 0,
};

type Step = {
  id: string;
  title: string;
  why: string;
  body: React.ReactNode;
};

const STEPS: Step[] = [
  {
    id: 'electoral-roll',
    title: 'Update your address on the electoral roll',
    why: 'Required by law — update within 21 days of becoming eligible (after a month at your new address) — and it puts you on the right state and federal ballot.',
    body: (
      <>
        <p style={{ margin: '0 0 0.5rem' }}>
          The Australian Electoral Commission (AEC) handles enrolment for both
          state and federal elections. One online form covers both.
        </p>
        <ul style={{ margin: '0 0 0.5rem 1.1rem', padding: 0, lineHeight: 1.55 }}>
          <li>
            <a href="https://check.aec.gov.au" style={link}>Check your enrolment</a>{' '}
            to see if your address is already up to date.
          </li>
          <li>
            <a href="https://www.aec.gov.au/enrol/" style={link}>Update your enrolment</a>{' '}
            online — takes about 5 minutes; needs a driver licence, passport or Medicare card — or someone already enrolled can confirm who you are.
          </li>
          <li>
            Once enrolled, your state vote sits in the <strong>Warren-Blackwood</strong>{' '}
            district. <Link to="/civic/enrol" style={link}>More on enrolment →</Link>
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'shire',
    title: 'Find your shire council',
    why: 'Your shire runs rates, bins, local roads, planning and most of the services you will use day-to-day.',
    body: (
      <>
        <p style={{ margin: '0 0 0.6rem' }}>
          Warren-Blackwood covers six shires. Open the page for yours to find
          rates, contacts, opening hours and the local councillor list.
        </p>
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: '0.35rem' }}>
          {SHIRES.map((s) => (
            <li key={s.id}>
              <Link
                to="/shire/$name"
                params={{ name: s.id }}
                style={{ ...link, fontWeight: 600 }}
              >
                Shire of {s.name} →
              </Link>
            </li>
          ))}
        </ul>
        <p style={{ margin: '0.7rem 0 0', fontSize: '0.9rem', color: 'var(--ink-mid)' }}>
          Not sure which shire you are in? Search your locality on the{' '}
          <Link to="/" style={link}>electorate map</Link>.
        </p>
      </>
    ),
  },
  {
    id: 'bins',
    title: 'Set up bin day reminders',
    why: 'Every shire runs its own collection calendar — first overflowing bin is a rite of passage.',
    body: (
      <>
        <p style={{ margin: '0 0 0.6rem' }}>
          Each council publishes its bin-day schedule online (most have a
          postcode or street-name lookup). Bookmark it on your phone, or set a
          weekly calendar reminder the night before collection.
        </p>
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: '0.35rem' }}>
          {SHIRES.filter((s) => s.quickLinks?.waste).map((s) => (
            <li key={s.id}>
              <a href={s.quickLinks!.waste} style={link}>
                {s.name} — bin day & waste ↗
              </a>
            </li>
          ))}
        </ul>
        <p style={{ margin: '0.7rem 0 0', fontSize: '0.9rem', color: 'var(--ink-mid)' }}>
          Most shires run a separate FOGO (food and garden organics) bin and
          quarterly verge collections — check your shire page for dates.
        </p>
      </>
    ),
  },
  {
    id: 'gp',
    title: 'Find a GP and register with a clinic',
    why: 'Rural GP books fill quickly. Register before you need an appointment, not after.',
    body: (
      <>
        <p style={{ margin: '0 0 0.5rem' }}>
          Most towns in the electorate have a GP clinic, but several are not
          taking new patients without a wait. Phone ahead, and ask whether they
          bulk-bill, take MyMedicare registrations, and run telehealth.
        </p>
        <ul style={{ margin: '0 0 0.5rem 1.1rem', padding: 0, lineHeight: 1.55 }}>
          <li>
            <a href="https://www.healthdirect.gov.au/australian-health-services" style={link}>
              Healthdirect Service Finder ↗
            </a>{' '}
            — search for GPs, pharmacies and allied health by postcode.
          </li>
          <li>
            <a href="https://www.healthdirect.gov.au/" style={link}>healthdirect 1800 022 222 ↗</a>{' '}
            — free 24-hour health advice line staffed by registered nurses.
          </li>
          <li>
            For after-hours emergencies, your nearest hospital is listed on
            each shire and locality page. In an emergency, dial <strong>000</strong>.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'school',
    title: 'Enrol your kids in school',
    why: 'WA public schools take enrolments any time; private and Catholic schools often have waitlists.',
    body: (
      <>
        <p style={{ margin: '0 0 0.5rem' }}>
          Each shire page lists the local primary, district high and senior
          high schools with website links. Enrol direct with the school.
        </p>
        <ul style={{ margin: '0 0 0.5rem 1.1rem', padding: 0, lineHeight: 1.55 }}>
          <li>
            <a href="https://www.education.wa.edu.au/web/our-schools/find-a-public-school" style={link}>
              WA Department of Education — find a public school ↗
            </a>{' '}
            with the catchment-area finder.
          </li>
          <li>
            <a href="https://www.education.wa.edu.au/enrolling-in-school" style={link}>
              Enrolling in a WA public school ↗
            </a>{' '}
            — what to bring (birth certificate, immunisation history, proof of address).
          </li>
          <li>
            For pre-school and childcare:{' '}
            <a href="https://www.startingblocks.gov.au/find-child-care" style={link}>
              Starting Blocks (federal) ↗
            </a>{' '}
            lists approved services by postcode.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'connect',
    title: 'Get connected — NBN and mobile',
    why: 'Coverage varies a lot across the electorate. Check before you sign a 24-month plan.',
    body: (
      <>
        <p style={{ margin: '0 0 0.5rem' }}>
          NBN technology type (FTTP, FTTN, Fixed Wireless, Sky Muster) depends
          on your exact address. Mobile reception varies block by block in some
          towns and most rural areas.
        </p>
        <ul style={{ margin: '0 0 0.5rem 1.1rem', padding: 0, lineHeight: 1.55 }}>
          <li>
            <a href="https://www.nbnco.com.au/connect-home-or-business/check-your-address" style={link}>
              Check your NBN address ↗
            </a>{' '}
            — shows the technology serving your home and ready-for-service date.
          </li>
          <li>
            Compare carrier coverage:{' '}
            <a href="https://www.telstra.com.au/coverage-networks/our-coverage" style={link}>Telstra ↗</a>{' '}
            ·{' '}
            <a href="https://www.optus.com.au/about/network/coverage-map" style={link}>Optus ↗</a>{' '}
            ·{' '}
            <a href="https://www.tpg.com.au/mobile/coverage" style={link}>TPG/Vodafone ↗</a>
          </li>
          <li>
            Each shire page has a <strong>Connectivity</strong> section with
            the NBN type for the main town and known mobile black spots.
          </li>
        </ul>
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', color: 'var(--ink-mid)' }}>
          Not confident with devices?{' '}
          <Link to="/services/digital-help" style={link}>Getting online, staying safe →</Link>
        </p>
      </>
    ),
  },
  {
    id: 'library',
    title: 'Join the local library',
    why: 'Free books, free WiFi, free meeting rooms, and the most useful informal help desk in any small town.',
    body: (
      <>
        <p style={{ margin: '0 0 0.5rem' }}>
          Every shire runs a public library — joining is free with proof of
          address. As well as books, libraries lend e-books and audiobooks
          through Libby/BorrowBox, run children's story time, and let you
          drop in to use a computer and printer.
        </p>
        <ul style={{ margin: '0 0 0.5rem 1.1rem', padding: 0, lineHeight: 1.55 }}>
          <li>
            <a href="https://slwa.wa.gov.au/find/public-libraries" style={link}>
              State Library of WA — find a public library ↗
            </a>{' '}
            — directory with opening hours.
          </li>
          <li>
            In the smaller shires (Boyup Brook, Nannup), the library is often
            co-located with the <strong>Community Resource Centre (CRC)</strong>
            {' '}— which also handles Service WA shopfront tasks, justices of the peace and printing.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'extras',
    title: 'A few extras worth doing in the first month',
    why: 'Small admin jobs that are easy to forget but pay off later.',
    body: (
      <>
        <ul style={{ margin: 0, paddingLeft: '1.1rem', lineHeight: 1.6 }}>
          <li>
            Update your address with the{' '}
            <a href="https://www.transport.wa.gov.au/" style={link}>
              WA Department of Transport ↗
            </a>{' '}
            (driver licence and vehicle registration).
          </li>
          <li>
            Update <a href="https://www.servicesaustralia.gov.au/individuals/online-help/centrelink/update-your-address" style={link}>Medicare and Centrelink ↗</a> via myGov.
          </li>
          <li>
            Sign up to your shire's emergency alerts and follow{' '}
            <a href="https://www.emergency.wa.gov.au" style={link}>Emergency WA ↗</a>{' '}
            — the south-west has a real bushfire season.{' '}
            <Link to="/emergency/fire-preparation" style={link}>Local fire prep →</Link>
          </li>
          <li>
            Find your nearest hospital, school and service town on the{' '}
            <Link to="/" style={link}>locality page</Link> for where you live.
          </li>
        </ul>
      </>
    ),
  },
];

function NewHerePage() {
  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          fontFamily: 'var(--font-sub)',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontSize: '0.72rem',
          color: 'var(--rivergum)',
          margin: '0 0 0.35rem',
        }}
      >
        New residents · Welcome
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5.5vw, 2.6rem)',
          color: 'var(--green)',
          margin: '0 0 0.4rem',
          lineHeight: 1.1,
        }}
      >
        New here? Welcome to Warren-Blackwood.
      </h1>
      <p style={{ color: 'var(--ink-mid)', margin: '0 0 var(--sp-md)', fontSize: '1.05rem', lineHeight: 1.55 }}>
        A short checklist for people who have just moved into one of the six
        shires of the Warren-Blackwood electorate. Work through it in any
        order — most steps take less than ten minutes.
      </p>

      <nav
        aria-label="Checklist"
        style={{
          ...card,
          background: 'var(--green-pale)',
          borderColor: 'var(--green)',
        }}
      >
        <p
          style={{
            margin: '0 0 0.5rem',
            fontFamily: 'var(--font-sub)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.72rem',
            color: 'var(--green)',
          }}
        >
          Jump to a step
        </p>
        <ol style={{ margin: 0, paddingLeft: '1.2rem', lineHeight: 1.6 }}>
          {STEPS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} style={link}>{s.title}</a>
            </li>
          ))}
        </ol>
      </nav>

      {STEPS.map((s, i) => (
        <section key={s.id} id={s.id} aria-labelledby={`${s.id}-h`} style={card}>
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
            <span style={stepNum} aria-hidden="true">{i + 1}</span>
            <h2 id={`${s.id}-h`} style={h2}>{s.title}</h2>
          </div>
          <p
            style={{
              margin: '0 0 0.7rem 2.6rem',
              fontSize: '0.92rem',
              color: 'var(--ink-mid)',
              fontStyle: 'italic',
              lineHeight: 1.5,
            }}
          >
            {s.why}
          </p>
          <div style={{ marginLeft: '2.6rem', fontSize: '0.97rem', lineHeight: 1.55 }}>
            {s.body}
          </div>
        </section>
      ))}

      <section style={{ ...card, background: 'var(--green-pale)', borderColor: 'var(--green)' }}>
        <h2 style={{ ...h2, margin: '0 0 0.4rem' }}>Still stuck?</h2>
        <p style={{ margin: '0 0 0.6rem', fontSize: '0.97rem', lineHeight: 1.55 }}>
          If you have tried the obvious channels and cannot get a state-government
          door to open — a hospital wait, a school transport issue, a state-housing
          query — that is what an electorate office is for.
        </p>
        <p style={{ margin: 0 }}>
          <Link to="/contact" style={{ ...link, fontWeight: 600 }}>
            Contact Bevan Eatts MP →
          </Link>
          {' · '}
          <Link to="/services/who-do-i-call" style={link}>
            Who do I call? (state vs federal vs council)
          </Link>
        </p>
      </section>
    </main>
  );
}
